import type { ReactNode } from 'react';
import { GaraLockup } from './GaraLogo';

interface AuthBrandPanelProps {
  eyebrow: string;
  title: ReactNode;
  body: ReactNode;
  children?: ReactNode;
}

/**
 * Left-hand brand panel on the auth screens. Always Void, whatever the theme:
 * the dark logo is the default and the panel is the one place it is shown at size.
 */
export function AuthBrandPanel({ eyebrow, title, body, children }: AuthBrandPanelProps) {
  return (
    <div className="relative hidden flex-[1.1] flex-col overflow-hidden bg-[#0B0C0C] p-11 text-[#ECEEED] lg:flex">
      {/* Graphite grid */}
      <svg className="absolute inset-0 h-full w-full opacity-60" aria-hidden="true">
        <defs>
          <pattern id="auth-grid" width="32" height="32" patternUnits="userSpaceOnUse">
            <path d="M32 0H0V32" fill="none" stroke="#1A1C1B" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#auth-grid)" />
      </svg>

      {/* Faint Signal glow — teal stays an accent, never a large area */}
      <div
        className="absolute -bottom-40 -right-40 h-[460px] w-[460px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(25,195,156,0.14), transparent 62%)' }}
        aria-hidden
      />

      <div className="relative">
        <GaraLockup size={30} />
      </div>

      <div className="relative mt-auto flex flex-col gap-5">
        <p className="font-mono text-[13px] text-[#8D9390]">
          {'// '}
          {eyebrow}
        </p>
        <h1 className="m-0 max-w-[480px] text-[44px] font-semibold leading-[1.05] tracking-tight">
          {title}
        </h1>
        <p className="m-0 max-w-[420px] text-[15px] leading-relaxed text-[#8D9390]">{body}</p>
        {children}
      </div>

      <div className="relative mt-8 flex items-center justify-between border-t border-[#2C302E] pt-5 font-mono text-xs text-[#8D9390]">
        <span>© 2026 garatech</span>
        <span>
          <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[#19C39C] align-middle" />
          built to hold
        </span>
      </div>
    </div>
  );
}
